import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styles from './EditorContent.module.scss';
import CardMenu from '../CardMenu/CardMenu';
import EditorRightSidebar from '../EditorRightSidebar/EditorRightSidebar';
import cx from 'classnames';
import { useUIContext } from '../../context/UIContext';
import { useIntroOutroContext } from '../../context/IntroOutroContext';
import { useTrimContext } from '../../context/VideoTrimContex';
import Spinner from '../../../common/components/Spinner';
import { useTranslation } from 'react-i18next';
import {
  TRANSITION_TYPE,
} from '../../constants/editorConst';
import { useTextPanelContext } from '../../context/TextPanelContext';
import { useUserContext } from '../../context/UserContext';
import ClipOrganizerPlayer from '../ClipOrganizerPlayer/ClipOrganizerPlayer';
import { useBackgroundMusicContext } from '../../context/BackgroundMusicContext';
import ClipsList from '../ClipsList/ClipsList';
import useMultiplyPlayer from '../../hooks/useMultiplyPlayer';
import { useZoomContext } from '../../context/ZoomContext';
import { useHistory } from 'react-router-dom';
import Container from '../../features/Editor/components/Container/Container';
import useFixedCardMenu from '../../features/Editor/hooks/useFixedCardMenu';
import useEditorResize from '../../features/Editor/hooks/useEditorResize';
import VideoService from 'engage/service/VideoService';
import { ToastNotification } from '../Toast/Toast';

const EditorContent = ({
  storyId,
  scenes,
  transitions,
  sceneIndex,
  videoByParts,
  currentVideoPart,
  handleEnded,
  handleSliderChange,
  handleSelectClip,
  onAddClip,
  onUploadClip,
  onAddEmptyClip,
  handleScenesMove,
  handleDeleteScene,
  handleDuplicateScene,
  handleSceneRename,
  handleTrimScene,
  handleIntroOutro,
  handleSaveChanges,
  handleSaveTextPanel,
  saving,
  video,
  logo,
  undoLastAction,
  redoLastAction,
  controlsDisabled,
  init,
  setSceneIndex,
  hasChanged,
  onAddTransition,
  onDeleteTransition,
  onTransitionChange,
  onBackgroundMusicChange,
  handleSpeedUpChange,
  updateSceneData,
  isStopBackgroundMusic,
  handleSplit,
  handleUpdateScene,
  handleRecordScene,
  userPermissions,
  handleCompleteAssignment,
  handleLogoChange,
  handleZoomPointsChange,
  assignments,
  handleReviseAssignment,
  handleCancelRecording,
  onEnhanceScene,
  onRemoveEnhanceScene,
  handleGenerateAvatarClip,
  videoConfig,
}) => {
  const history = useHistory();
  const [rename, setRename] = useState({ flag: false });
  const [toggleTrimming, setToggleTrimming] = useState(false);
  const [switchingTrimming, setSwitchingTrimming] = useState(false);
  const [isDraft, setIsDraft] = useState(false);

  const [activeTransitionRightMedia, setActiveTransitionRightMedia] = useState(null);
  const [currentTransitionType, setCurrentTransitionType] = useState(TRANSITION_TYPE.DIP_TO_WHITE);
  const [disableNonClipCards, setDisableNonClipCards] = useState(false);
  const [isEditingRightSidebar, setIsEditingRightSidebar] = useState(false);
  const isSceneProcessing = useMemo(() => Boolean(scenes?.find(item => item.processing && (item.uploaded || item.selected))), [
    scenes,
  ]);

  const { showMenu, menuCoordinates, menuRef, toggleShowMenu, onOpenMenu } = useFixedCardMenu();

  const [miniRightSidebar, setMiniRightSidebar] = useState(false);
  const [videoSliderChangedFlag, setVideoSliderChangedFlag] = useState(false);

  const pageContentRef = useRef();

  const videoSliderChangeTimestamp = useRef();

  const { trimmingOn, toggleTrimmingOn, startTrimStamp, endTrimStamp, clearAfterTrimmingDone } = useTrimContext();

  const { isZoomOpened, zooms, initialZooms } = useZoomContext();

  const { miniSidebar } = useUIContext();

  const { user, appConfig } = useUserContext();

  const { changeInitialIntroOutro, setVideoOrientation } = useIntroOutroContext();

  const { videoElements, setSeekPlayerTo, currentPlayerKey } = useMultiplyPlayer({
    scenes,
    sceneIndex,
  });

  const [isTransitionExists, setIsTransitionExists] = useState(false);

  const [isRecording, setIsRecording] = useState(false);

  const isVideoOwner = useMemo(() => {
    if (video?.author && user?.username) {
      return video?.author.username === user.username;
    }
  }, [video, user]);

  const selectedSceneAssignment = useMemo(() => assignments.find(item => item.sceneId === showMenu.id), [assignments, showMenu?.id]);

  const isSelectedDraftScene = useMemo(() => !scenes?.find(scene => scene.id === showMenu.id)?.video, [scenes, showMenu]);

  useEffect(() => {
    if (videoByParts?.scenes?.length) {
      setIsTransitionExists(
        transitions?.find(item => item.leftMediaId === videoByParts?.scenes[Number(currentVideoPart)]?.id) !== undefined
      );
    }
  }, [transitions, videoByParts, currentVideoPart]);

  const isCurrentAssignedScenesCompleted = useMemo(() => {
    if (scenes && user) {
      return scenes
        .filter(item => Boolean(item.author) && item?.author?.username === user.username)
        .every(item => item.isNew || item.isCompleted);
    }
  }, [scenes, user]);

  const isCurrentAssignedScenesRecorded = useMemo(() => {
    if (scenes && user) {
      return scenes.filter(item => Boolean(item.author) && item?.author?.username === user.username).every(item => item.video);
    }
  }, [scenes, user]);

  const isAllowedToDownload = useMemo(() => {
    if (!showMenu.isDraft && !showMenu.isProcessing) {
      const { isOwner, folderEditor, updateStory, allowedScenes } = userPermissions;
      // If user has editing rights for the story
      if (isOwner || folderEditor || updateStory) {
        return true;
      }

      // If user has rights for the scene
      if (allowedScenes.length && allowedScenes?.find(id => id === showMenu?.id)) {
        return true;
      }
    }
    return false;
  }, [showMenu?.id, isVideoOwner, isUpdateAllowed, selectedSceneAssignment, user, userPermissions]);

  const collaboratingUsers = useMemo(
    () =>
      scenes
        .filter(item => Boolean(item.author) && item?.author?.username !== video?.author.username)
        .map(item => item.author)
        .filter((user, index, array) => array.findIndex(item => item.username === user.username) === index),
    [scenes]
  );

  const isUpdateAllowed = useMemo(() => userPermissions?.updateStory, [userPermissions]);

  const {
    isBackgroundMusicOpened,
    selectedMusic,
    musicVolume,
    videoVolume,
    setAvailableMusic,
    isApplyAllTrackEnabled,
    isApplyAllVolumeEnabled,
  } = useBackgroundMusicContext();

  const { clipsListStyles, editorRef } = useEditorResize({
    playerClassName: 'MultipleVideosPlayer',
    miniSidebar,
    miniRightSidebar,
    videoSliderChangedFlag,
    pageContentRef,
  });

  const { t } = useTranslation();

  const { isTextPanelEditor, initTextPanelsData, topText, bottomText, currentTemplate, resetEditor, enableTextPanelEditor } = useTextPanelContext();

  useEffect(() => {
    const { scenes: scenesFromParts } = videoByParts;
    if (scenesFromParts?.length) {
      const hasEmptyVideo = videoByParts?.scenes.some(item => item.video === null);
      setIsDraft(hasEmptyVideo);
    }
  }, [videoByParts]);

  const handleRenameScene = () => {
    const sceneName = scenes?.find(scene => scene.id === showMenu.id)?.name;
    setRename({ flag: true, id: showMenu.id, name: sceneName });
  };

  const saveNewSceneName = name => {
    const renamedScene = scenes?.find(scene => scene.id === rename.id);
    setRename({ flag: false });
    handleSceneRename(renamedScene, { name, nameChanged: true });
  };

  const handleCancelRename = () => {
    setRename({ flag: false });
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    handleScenesMove(oldIndex, newIndex);
  };

  const handleRenameEnabled = useCallback(() => {
    if (rename?.flag) {
      const confirmSwitching = confirm(t('clip_organizer.confirm_switch_rename'));
      if (confirmSwitching) {
        handleCancelRename();
      } else {
        return false;
      }
    }
    return true;
  }, [handleCancelRename, rename]);

  const onDeleteScene = useCallback(() => {
    if (handleRenameEnabled()) {
      const sceneToDelete = scenes?.find(item => item.id === showMenu.id);
      const index = scenes?.indexOf(sceneToDelete);
      handleDeleteScene(sceneToDelete, index);
    }
  }, [scenes, handleRenameEnabled, handleDeleteScene, showMenu]);

  const onDuplicateScene = useCallback(() => {
    if (handleRenameEnabled()) {
      const sceneToDuplicate = scenes?.find(item => item.id === showMenu.id);
      if (sceneToDuplicate) {
        handleDuplicateScene(sceneToDuplicate);
      }
    }
  }, [scenes, handleDuplicateScene, handleRenameEnabled, showMenu]);

  const onDownloadScene = useCallback(async () => {
    const scene = scenes.find(item => item.id === showMenu?.id);
    try {
      const { normalizedUrl } = await VideoService.getDownloadSceneUrl(storyId, scene?.originalId ?? scene.id);
      ToastNotification({
        message: t('clip_organizer.toast.success_download_scene'),
        iconName: 'success',
      })
      const videoInfo = { url: normalizedUrl, name: scene.name };
      await VideoService.downloadVideo([videoInfo], () => { });
    } catch (e) {
      ToastNotification({
        message: t('clip_organizer.toast.error_download_scene'),
      });
    }
  }, [scenes, showMenu]);

  const startTrimming = useCallback(() => {
    setToggleTrimming(true);
    handleSelectClip(currentVideoPart);
  }, [handleRenameEnabled]);

  useEffect(() => {
    if (toggleTrimming) {
      toggleTrimmingOn(true);
      setToggleTrimming(false);
    }
  }, [toggleTrimming]);

  const handleTrimVideo = useCallback(() => {
    handleTrimScene(
      Number(currentVideoPart),
      startTrimStamp * (scenes[sceneIndex]?.playbackRate || 1),
      endTrimStamp * (scenes[sceneIndex]?.playbackRate || 1)
    );
    toggleTrimmingOn();
  }, [toggleTrimmingOn, handleTrimScene, currentVideoPart, startTrimStamp, endTrimStamp]);

  const handleSplitVideo = useCallback(
    (value, trimStart, trimEnd) => {
      handleSplit(Number(currentVideoPart), value, trimStart, trimEnd);
      setToggleTrimming(true);
    },
    [toggleTrimmingOn, currentVideoPart, handleSplit]
  );

  const onSaveTextPanel = useCallback(
    (topText, bottomText, template) => {
      handleSaveTextPanel(Number(currentVideoPart), topText, bottomText, template);
    },
    [scenes, sceneIndex, currentVideoPart, handleSaveTextPanel]
  );

  const handleSelectClipCard = useCallback(
    index => {
      // If clip card is already selected - just return
      if (index === currentVideoPart && !activeTransitionRightMedia) {
        return;
      }

      setActiveTransitionRightMedia(null);
      if (trimmingOn) {
        handleTrimVideo();
        clearAfterTrimmingDone();
        setSwitchingTrimming(true);
        setTimeout(() => {
          setToggleTrimming(true);
          toggleTrimmingOn(true);
          setSwitchingTrimming(false);
        }, 100);
        handleSelectClip(index);
      } else if (isTextPanelEditor) {
        // Save changes for scene
        onSaveTextPanel(topText, bottomText, currentTemplate);
        resetEditor();
        const newSceneId = scenes[parseInt(index) + (videoByParts?.intro ? 1 : 0)]?.id;
        enableTextPanelEditor(newSceneId);
        handleSelectClip(index);
      } else if (isBackgroundMusicOpened) {
        // Don't change music when user selects another clip
        onBackgroundMusicChange({
          sceneIndex: Number(currentVideoPart),
          newMusic: selectedMusic,
          musicVolume: musicVolume / 100,
          videoVolume: videoVolume / 100,
          isApplyAllTrackEnabled,
          isApplyAllVolumeEnabled,
        });

        // add a slight delay to smoothly change the music UI in the selected clip
        setTimeout(() => handleSelectClip(index), 50);
      } else if (isZoomOpened) {
        // we changed the zoom values based on the current scene settings (scene start and scene speed) and these values were mutated
        // So we need to convert back to real values.
        const realZooms = zooms?.map(zoom => {
          const realOffset = zoom.offset * (scenes[sceneIndex]?.playbackRate || 1) + scenes[sceneIndex]?.trimStart;
          const realDuration = zoom.duration * (scenes[sceneIndex]?.playbackRate || 1);

          return {
            ...zoom,
            offset: realOffset,
            duration: realDuration,
          };
        });

        const realInitialZooms = initialZooms?.map(initialZoom => {
          const realOffset = initialZoom.offset * (scenes[sceneIndex]?.playbackRate || 1) + scenes[sceneIndex]?.trimStart;
          const realDuration = initialZoom.duration * (scenes[sceneIndex]?.playbackRate || 1);

          return {
            ...initialZoom,
            offset: realOffset,
            duration: realDuration,
          };
        });

        handleZoomPointsChange({
          sceneIndex: Number(currentVideoPart),
          zoomPoints: realZooms,
          initialZoomPoints: realInitialZooms,
          registerAction: true,
        });

        handleSelectClip(index);
      } else {
        if (handleRenameEnabled()) {
          handleSelectClip(index);
        }
      }
    },
    [
      trimmingOn,
      currentTemplate,
      topText,
      bottomText,
      isZoomOpened,
      handleSelectClip,
      handleRenameEnabled,
      setActiveTransitionRightMedia,
      currentVideoPart,
      activeTransitionRightMedia,
      isApplyAllTrackEnabled,
      isApplyAllVolumeEnabled,
      onBackgroundMusicChange,
    ]
  );

  const handleChangeTransition = newType => {
    onTransitionChange(activeTransitionRightMedia, newType);
  };

  const handleDisableNonClipCards = useCallback(
    value => {
      if (value) {
        if (activeTransitionRightMedia === null) {
          if (currentVideoPart === 'intro' || currentVideoPart === 'outro') {
            handleSelectClip('0');
          }
        } else {
          const transitionsRightScene = videoByParts.scenes.find(item => item.id === activeTransitionRightMedia);
          const sceneIndex = videoByParts.scenes.indexOf(transitionsRightScene);
          handleSelectClip(sceneIndex.toString());
          setActiveTransitionRightMedia(null);
        }
      }
      setDisableNonClipCards(value);
    },
    [currentVideoPart, activeTransitionRightMedia, handleSelectClip]
  );

  const handleRequestRevise = useCallback(() => {
    handleReviseAssignment(selectedSceneAssignment?.assigned);
  }, [selectedSceneAssignment]);

  const handleEditStoryboard = () => {
    history.push(`/eg/storyboard/${storyId}`);
  };

  const filteredScenes = useMemo(() => scenes.filter(item => !item.hidden), [scenes]);

  useEffect(() => {
    if (activeTransitionRightMedia !== null) {
      const currentTransition = transitions.find(item => item.rightMediaId === activeTransitionRightMedia);
      if (currentTransition) {
        setCurrentTransitionType(currentTransition.type);
      }
    }
  }, [activeTransitionRightMedia, transitions]);

  useEffect(() => {
    if (video) {
      setVideoOrientation(video.orientation);
    }
  }, [video]);

  useEffect(() => {
    setAvailableMusic(appConfig?.playlist?.map(track => ({ ...track, path: track.music_url })) || []);
  }, [appConfig]);

  useEffect(() => {
    if (videoSliderChangedFlag === (trimmingOn || isZoomOpened)) {
      if (videoSliderChangeTimestamp?.current) {
        clearTimeout(videoSliderChangeTimestamp.current);
        videoSliderChangeTimestamp.current = null;
      }
    } else {
      if (videoSliderChangeTimestamp?.current) {
        clearTimeout(videoSliderChangeTimestamp.current);
        videoSliderChangeTimestamp.current = null;
      } else {
        videoSliderChangeTimestamp.current = setTimeout(() => {
          setVideoSliderChangedFlag(trimmingOn || isZoomOpened);
        }, 100);
      }
    }
  }, [trimmingOn, isZoomOpened]);

  useEffect(() => {
    if (video) {
      const intro = video?.introMeta
        ? {
          id: video?.introMeta?.introOutroId,
          video: video?.intro,
          thumbnail: video?.introMeta?.thumbnail,
          duration: video?.introMeta?.duration,
        }
        : null;

      const outro = video?.outroMeta
        ? {
          id: video?.outroMeta?.introOutroId,
          video: video?.outro,
          thumbnail: video?.outroMeta?.thumbnail,
          duration: video?.outroMeta?.duration,
        }
        : null;

      handleIntroOutro({ introOutro: { intro: structuredClone(intro), outro: structuredClone(outro) } });

      changeInitialIntroOutro({ intro, outro });
    }
  }, [video]);

  useEffect(() => {
    return () => {
      toggleTrimmingOn(false);
      clearAfterTrimmingDone();
    };
  }, []);

  useEffect(() => {
    if (isTextPanelEditor && currentVideoPart !== 'intro' && currentVideoPart !== 'outro') {
      if (scenes[sceneIndex]) {
        let presetTemplateId = null;
        if (!scenes[sceneIndex]?.nameCard) {
          // Define text panel style to select
          // * there’s only one style: Set that style as the selected one
          // * if there’s 2 different styles: Select the default and once the user selects another in the same session use the latest selected in that session. (If user selects style A, then for the next text panel style A should be selected)
          const scenesWithTextPanels = scenes.filter(item => item?.nameCard);
          const changedDuringCurrentSession = scenesWithTextPanels.filter(item => item.textPanelChanged);
          if (changedDuringCurrentSession.length) {
            presetTemplateId = changedDuringCurrentSession.length > 1 ?
              changedDuringCurrentSession.filter(({ nameCard = null }) => nameCard != null && nameCard?.templateId != null)
                .map(({ nameCard = null }) => nameCard)
                ?.sort((a, b) => b.selectedDate - a.selectedDate)[0]?.templateId
              : changedDuringCurrentSession[0].nameCard?.templateId;
          } else {
            const uniqueStyles = [...new Set(scenesWithTextPanels.filter(item => item.nameCard?.templateId).map(({ templateId }) => templateId))];
            if (uniqueStyles.length === 1) {
              presetTemplateId = uniqueStyles[0];
            }
          }
        }
        initTextPanelsData({ ...scenes[sceneIndex]?.nameCard, ...(presetTemplateId ? { templateId: presetTemplateId } : {}) });
      }
    }
  }, [isTextPanelEditor, scenes, sceneIndex]);

  useEffect(() => {
    const currentTransition = transitions.find(item => item.rightMediaId === activeTransitionRightMedia);
    if (!currentTransition) {
      setActiveTransitionRightMedia(null);
    }
  }, [transitions]);

  useEffect(() => {
    const storageListener = e => {
      if (e.key === 'isClipRecording') {
        setIsRecording(JSON.parse(e.newValue));
      }
    };

    window.addEventListener('storage', storageListener);

    return () => window.removeEventListener('storage', storageListener);
  }, []);

  return (
    <Container innerRef={pageContentRef} className={cx(styles.editContent, { [styles.miniSidebar]: miniSidebar })}>
      {init ? (
        <Spinner className={styles.spinner} width="30px" height="30px" size="3px" />
      ) : (
        <div className={styles.wrapper}>
          <ClipOrganizerPlayer
            video={video}
            storyId={video?.id}
            scenes={scenes}
            transitions={transitions}
            sceneIndex={sceneIndex}
            switchingTrimming={switchingTrimming}
            saving={saving}
            intro={videoByParts?.intro}
            outro={videoByParts?.outro}
            logo={logo}
            draft={isDraft}
            author={video?.author}
            hasChanged={hasChanged}
            setSceneIndex={setSceneIndex}
            handleEnded={handleEnded}
            handleSliderChange={handleSliderChange}
            updateSceneData={updateSceneData}
            isStopBackgroundMusic={isStopBackgroundMusic}
            userPermissions={userPermissions}
            videoElements={videoElements}
            currentPlayerKey={currentPlayerKey}
            setSeekPlayerTo={setSeekPlayerTo}
            miniRightSidebar={miniRightSidebar}
            isRecordingGeneral={isRecording}
            isEditingRightSidebar={isEditingRightSidebar}
            handleRecordScene={handleRecordScene}
            innerRef={editorRef}
            handleCancelRecording={handleCancelRecording}
            handleGenerateAvatarClip={handleGenerateAvatarClip}
            videoConfig={videoConfig}
          />
          <ClipsList
            isDraft={isDraft}
            intro={videoByParts?.intro}
            outro={videoByParts?.outro}
            cards={filteredScenes}
            currentVideoPart={currentVideoPart}
            transitions={transitions}
            handleDotsClick={onOpenMenu}
            handleSelectClip={handleSelectClipCard}
            showMenu={showMenu}
            onSortEnd={onSortEnd}
            saving={saving}
            onAddClip={onAddClip}
            onUploadClip={onUploadClip}
            onAddEmptyClip={onAddEmptyClip}
            onAddTransition={onAddTransition}
            handleRecordScene={handleRecordScene}
            activeTransitionRightMedia={activeTransitionRightMedia}
            setActiveTransitionRightMedia={setActiveTransitionRightMedia}
            onDeleteTransition={onDeleteTransition}
            onBackgroundMusicChange={onBackgroundMusicChange}
            disableNonClipCards={disableNonClipCards}
            userPermissions={userPermissions}
            isEditingRightSidebar={isEditingRightSidebar}
            className={styles.clipsListContainer}
            clipsListStyles={clipsListStyles}
            hasChanged={hasChanged}
          />
        </div>
      )}
      {showMenu.flag ? (
        <CardMenu
          customStyles={{ top: menuCoordinates.y, left: menuCoordinates.x }}
          menuRef={menuRef}
          listMode={false}
          item={videoByParts.scenes[showMenu.index]}
          itemIndex={showMenu.index}
          toggleShowMenu={toggleShowMenu}
          renameItem={isUpdateAllowed && !showMenu.isProcessing && handleRenameScene}
          deleteVideo={isUpdateAllowed && videoByParts.scenes?.length > 1 && onDeleteScene}
          duplicateItem={isUpdateAllowed && !showMenu.isProcessing && onDuplicateScene}
          downloadItem={isAllowedToDownload && { action: onDownloadScene }}
          handleRequestRevise={isVideoOwner && !isSelectedDraftScene && selectedSceneAssignment && handleRequestRevise}
        />
      ) : null}
      <EditorRightSidebar
        storyId={storyId}
        video={video}
        logo={logo}
        isDraft={isDraft}
        init={init}
        videoByParts={videoByParts}
        currentVideoPart={currentVideoPart}
        sceneIndex={sceneIndex}
        activeScene={scenes[sceneIndex]}
        rename={rename}
        handleRename={saveNewSceneName}
        handleSaveClip={handleSaveChanges}
        saving={saving}
        isSceneProcessing={isSceneProcessing}
        cancelEditing={handleCancelRename}
        handleUndo={undoLastAction}
        handleRedo={redoLastAction}
        controlsDisabled={controlsDisabled}
        trimmingMode={trimmingOn}
        handleTrimVideo={handleTrimVideo}
        hasChanged={hasChanged}
        handleStartTrimming={startTrimming}
        handleSplit={handleSplitVideo}
        switchingTrimming={switchingTrimming}
        isTransitionExists={isTransitionExists}
        isTransitionAllowed={videoByParts?.scenes?.length - 1 !== Number(currentVideoPart)}
        activeTransitionRightMedia={activeTransitionRightMedia}
        onTransitionChanged={handleChangeTransition}
        currentTransitionType={currentTransitionType}
        handleSaveTextPanel={onSaveTextPanel}
        handleIntroOutro={handleIntroOutro}
        onBackgroundMusicChange={onBackgroundMusicChange}
        handleSpeedUpChange={handleSpeedUpChange}
        isVideoHasHls={Boolean(video?.videoHls)}
        toggleDisableNonClipCards={handleDisableNonClipCards}
        handleUpdateScene={handleUpdateScene}
        userPermissions={userPermissions}
        handleLogoChange={handleLogoChange}
        handleZoomPointsChange={handleZoomPointsChange}
        assignments={assignments}
        miniRightSidebar={miniRightSidebar}
        setMiniRightSidebar={setMiniRightSidebar}
        addTransitionToScene={onAddTransition}
        isRecording={isRecording}
        handleIsEditing={setIsEditingRightSidebar}
        handleEditStoryboard={handleEditStoryboard}
        handleCompleteAssignment={handleCompleteAssignment}
        isCurrentAssignedScenesCompleted={isCurrentAssignedScenesCompleted}
        isCurrentAssignedScenesRecorded={isCurrentAssignedScenesRecorded}
        collaboratingUsers={collaboratingUsers}
        onEnhanceScene={onEnhanceScene}
        onRemoveEnhanceScene={onRemoveEnhanceScene}
      />
    </Container>
  );
};

export default EditorContent;
